import { z } from 'zod'

const text = (max: number) => z.string().trim().max(max)
const optionalUrl = z.string().trim().max(2000).refine(
  value => !value || /^https:\/\/[^\s]+$/i.test(value),
  'Bitte einen vollständigen HTTPS-Link angeben.',
).optional()

export const manufacturerGuaranteeProgramSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/),
  enabled: z.boolean(),
  title: text(160),
  scope: z.enum(['allProducts', 'limited']),
  scopeDescription: text(2000),
  // durability means the manufacturer's free durability guarantee for the whole good.
  kind: z.enum(['other', 'durability']),
  durationYears: z.number().min(2.5).max(99).multipleOf(0.5).optional(),
  durationText: text(500),
  guarantorName: text(250),
  guarantorAddress: text(2000),
  territory: text(1500),
  claimProcedure: text(6000),
  conditionsText: text(40000),
  conditionsUrl: optionalUrl,
  sourceConfirmed: z.boolean(),
  // Confirmed manufacturer identifiers, never internal product IDs or guessed titles.
  modelSource: z.enum(['mpn', 'sku']).optional(),
}).superRefine((program, ctx) => {
  if (!program.enabled) return
  const required = [
    'title', 'scopeDescription', 'durationText', 'guarantorName',
    'guarantorAddress', 'territory', 'claimProcedure', 'conditionsText',
  ] as const
  for (const field of required) {
    if (!program[field]) ctx.addIssue({ code: 'custom', path: [field], message: 'Für die Veröffentlichung erforderlich.' })
  }
  if (!program.sourceConfirmed) ctx.addIssue({ code: 'custom', path: ['sourceConfirmed'], message: 'Die Angaben müssen anhand der Herstellerbedingungen bestätigt werden.' })
  if (program.kind === 'durability') {
    if (program.scope !== 'allProducts') ctx.addIssue({ code: 'custom', path: ['scope'], message: 'Unterschiedliche Ganzwaren-Garantien benötigen eine sichere Artikelzuordnung. Dieses Programm bitte als Entwurf speichern.' })
    if (!program.durationYears) ctx.addIssue({ code: 'custom', path: ['durationYears'], message: 'Für GARAN ist die bestätigte Gesamtdauer von mehr als zwei Jahren erforderlich.' })
    if (!program.modelSource) ctx.addIssue({ code: 'custom', path: ['modelSource'], message: 'Die Quelle der tatsächlichen Hersteller-Modellkennung bestätigen.' })
  }
})
export type ManufacturerGuaranteeProgram = z.infer<typeof manufacturerGuaranteeProgramSchema>

export const manufacturerGuaranteeProgramsSchema = z.array(manufacturerGuaranteeProgramSchema).max(10).superRefine((programs, ctx) => {
  if (new Set(programs.map(program => program.id)).size !== programs.length) {
    ctx.addIssue({ code: 'custom', message: 'Garantieprogramme müssen unterschiedliche Kennungen haben.' })
  }
  if (programs.filter(program => program.enabled && program.kind === 'durability').length > 1) {
    ctx.addIssue({ code: 'custom', message: 'Nur eine einheitliche Ganzwaren-Haltbarkeitsgarantie kann automatisch für das gesamte Sortiment gelten.' })
  }
})

export const garanLabelDataSchema = z.object({
  years: z.number().min(2.5).max(99).multipleOf(0.5),
  producer: z.string().min(1).max(250),
  model: z.string().min(1).max(250),
})
export type GaranLabelData = z.infer<typeof garanLabelDataSchema>

export const manufacturerGuaranteeSnapshotSchema = z.object({
  manufacturerId: z.number(),
  programId: z.string(),
  revision: z.string(),
  title: z.string(),
  scope: z.enum(['allProducts', 'limited']),
  scopeDescription: z.string(),
  kind: z.enum(['other', 'durability']),
  durationText: z.string(),
  guarantorName: z.string(),
  guarantorAddress: z.string(),
  territory: z.string(),
  claimProcedure: z.string(),
  conditionsText: z.string(),
  conditionsUrl: optionalUrl,
  label: garanLabelDataSchema.optional(),
})
export type ManufacturerGuaranteeSnapshot = z.infer<typeof manufacturerGuaranteeSnapshotSchema>

export const productGuaranteeInfoSchema = z.object({
  id: z.number(),
  key: z.string(),
  name: z.string(),
  guarantees: z.array(manufacturerGuaranteeSnapshotSchema),
})
export type ProductGuaranteeInfo = z.infer<typeof productGuaranteeInfoSchema>
export const productGuaranteesResponseSchema = z.object({ items: z.array(productGuaranteeInfoSchema) })

export const statutoryGuaranteeRightsText = 'Ihre gesetzlichen Rechte bei Mängeln gegenüber dem Verkäufer können Sie unentgeltlich in Anspruch nehmen. Diese Rechte werden durch die Herstellergarantie nicht eingeschränkt.'

export const newManufacturerGuaranteeProgram = (id: string, name = '', address = ''): ManufacturerGuaranteeProgram => ({
  id, enabled: false, title: 'Herstellergarantie', scope: 'allProducts',
  scopeDescription: 'Alle Produkte dieses Herstellers', kind: 'other', durationText: '',
  guarantorName: name, guarantorAddress: address, territory: '', claimProcedure: '',
  conditionsText: '', conditionsUrl: '', sourceConfirmed: false,
})
